/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import { TONES, TONE_DO_STATUS } from '../paleta';
import { ST_META, ordenarParaExibicao } from '../utils/ordemStatus';
import type { IEmpresaComStatus } from '../utils/derivarProcessos';
import { NomeEmpresa } from './NomeEmpresa';

/**
 * Card "Fila" da aba Processos — espelho 1:1 do protótipo (`sistema/index.html`
 * linhas 227-306) nos três formatos por nível:
 * - nano: lista simples (flat);
 * - board: agrupadores por BOT (header roxo, ABERTO por padrão);
 * - macro: faixas de Departamento (header verde, ABERTO por padrão) → agrupadores de
 *   Grupo (header roxo, FECHADO por padrão — toggle com valor explícito, o mesmo
 *   `setConcluidosColapsado` que evita o bug dos 2 cliques do protótipo).
 * Chaves de colapso prefixadas (`dep:`/`grp:`/`bot:`) como no original.
 *
 * Desvio deliberado do protótipo: cada lista de empresas (flat do nano e conteúdo
 * expandido de cada faixa bot:/grp:) rola dentro do próprio bloco (`.orq-fila-lista`,
 * max-height no prototipo.css) para a página não crescer com listas longas — a
 * contagem "N empresas" no header da faixa segue dando o total.
 *
 * Ordenação (correção de 11/09): as listas usam `ordenarParaExibicao` (falha/alerta
 * primeiro), não a ordem de dedup `ordenarPorStatus` (`done` no topo). O bloco só
 * mostra ~12 linhas antes de rolar, então numa faixa de centenas de empresas as
 * falhas ficavam invisíveis atrás das concluídas — o mesmo bug do popup, agravado
 * pela janela menor. A contagem do header não muda: vem de `g.companies.length`,
 * sobre a lista de origem.
 */

export interface FilaGrupo {
  chave: string;
  nome: string;
  /** Tooltip do header — o NOME REAL do bot quando `nome` é um apelido de exibição. */
  tituloReal?: string;
  companies: IEmpresaComStatus[];
}

export interface FilaFaixa {
  chave: string;
  nome: string;
  grupos: FilaGrupo[];
}

export type FilaDados =
  | { formato: 'flat'; label: string; companies: IEmpresaComStatus[] }
  | { formato: 'grupos'; label: string; grupos: FilaGrupo[] }
  | { formato: 'departamentos'; label: string; faixas: FilaFaixa[] };

const rotuloStatus = (c: IEmpresaComStatus): string => {
  const base = ST_META[c.st]?.label ?? ST_META.wait.label;
  return c.qtdBots && c.qtdBots > 1 ? `${base} (${c.qtdBots})` : base;
};

/** Linha de empresa: dot 7px + nome_código + statusLabel colorido (linhas 237-241). */
const LinhaEmpresa: React.FC<{ c: IEmpresaComStatus }> = ({ c }) => {
  const t = TONES[TONE_DO_STATUS[c.st] ?? 'idle'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
      <span
        style={{
          width: '7px',
          height: '7px',
          flex: 'none',
          borderRadius: '50%',
          background: t.dot,
        }}
      />
      <NomeEmpresa
        variante="sufixo"
        nome={c.nome}
        codigo={c.codiEmp}
        style={{
          fontSize: '12.5px',
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      />
      <span style={{ fontSize: '11px', color: t.fg, flex: 'none' }}>{rotuloStatus(c)}</span>
    </div>
  );
};

const contagemLabel = (n: number): string => (n === 1 ? '1 empresa' : `${n} empresas`);

interface FilaCardProps {
  dados: FilaDados;
}

export const FilaCard: React.FC<FilaCardProps> = ({ dados }) => {
  /** Estado de colapso por chave prefixada — persiste entre níveis, como no protótipo. */
  const [colapsados, setColapsados] = useState<Record<string, boolean | undefined>>({});

  const toggle = (chave: string) => setColapsados(atual => ({ ...atual, [chave]: !atual[chave] }));
  const setColapsado = (chave: string, valor: boolean) =>
    setColapsados(atual => ({ ...atual, [chave]: valor }));

  /** Agrupador de GRUPO (header roxo) — fechado por padrão quando `padraoFechado`. */
  const blocoGrupo = (g: FilaGrupo, padraoFechado: boolean) => {
    const cru = colapsados[g.chave];
    let collapsed: boolean;
    if (padraoFechado) collapsed = cru === undefined ? true : cru;
    else collapsed = Boolean(cru);
    const companies = collapsed ? [] : ordenarParaExibicao(g.companies);
    return (
      <div
        key={g.chave}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          breakInside: 'avoid',
          marginBottom: '14px',
        }}
      >
        <div
          onClick={() => (padraoFechado ? setColapsado(g.chave, !collapsed) : toggle(g.chave))}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            background: 'oklch(0.94 0.025 295)',
            cursor: 'pointer',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '14px',
                height: '14px',
                flex: 'none',
                color: 'oklch(0.4 0.09 295)',
                fontSize: '9px',
                transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                transition: 'transform 0.15s',
              }}
            >
              ▶
            </span>
            <span
              title={g.tituloReal}
              style={{
                fontSize: '12.5px',
                fontWeight: 700,
                color: 'oklch(0.32 0.09 295)',
                letterSpacing: '-0.005em',
              }}
            >
              {g.nome}
            </span>
          </span>
          <span
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '10.5px',
              fontWeight: 600,
              color: 'oklch(0.45 0.09 295)',
              flex: 'none',
            }}
          >
            {contagemLabel(g.companies.length)}
          </span>
        </div>
        {companies.length > 0 && (
          <div
            className="orq-fila-lista"
            style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
          >
            {companies.map(c => (
              <LinhaEmpresa key={`${g.chave}:${c.codiEmp}`} c={c} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        background: 'oklch(1 0 0)',
        border: '1px solid oklch(0.92 0.006 160)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ fontSize: '16px', fontWeight: 600 }}>Fila</div>
        <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>{dados.label}</div>
      </div>

      {dados.formato === 'flat' && (
        <div
          className="orq-fila-lista"
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          {ordenarParaExibicao(dados.companies).map(c => (
            <LinhaEmpresa key={c.codiEmp} c={c} />
          ))}
        </div>
      )}

      {dados.formato === 'grupos' && (
        <div style={{ columnCount: 2, columnGap: '20px' }}>
          {dados.grupos.map(g => blocoGrupo(g, false))}
        </div>
      )}

      {dados.formato === 'departamentos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {dados.faixas.map(d => {
            const collapsedDep = Boolean(colapsados[d.chave]);
            return (
              <div key={d.chave} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div
                  onClick={() => toggle(d.chave)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'oklch(0.95 0.025 145)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '14px',
                        height: '14px',
                        flex: 'none',
                        color: 'oklch(0.35 0.09 145)',
                        fontSize: '9px',
                        transform: collapsedDep ? 'rotate(0deg)' : 'rotate(90deg)',
                        transition: 'transform 0.15s',
                      }}
                    >
                      ▶
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'oklch(0.28 0.09 145)',
                        letterSpacing: '-0.005em',
                      }}
                    >
                      {d.nome}
                    </span>
                  </span>
                </div>
                {!collapsedDep && (
                  <div style={{ columnCount: 2, columnGap: '20px', paddingLeft: '10px' }}>
                    {d.grupos.map(g => blocoGrupo(g, true))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
